import { useMemo } from 'react';
import { Box, SimpleGrid, useColorModeValue } from '@chakra-ui/react';
import Card from 'components/card/Card';
import BarChart from 'components/charts/BarChart';
import LineChart from 'components/charts/LineChart';
import FinanceFilters from 'components/finance/FinanceFilters';
import CategoryPieCard from 'components/finance/CategoryPieCard';
import { useFinance } from 'contexts/FinanceContext';
import {
  barChartOptionsConsumption,
  lineChartOptionsTotalSpent,
} from 'variables/charts';

export default function Reports() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const { monthlyData, balanceOverTime } = useFinance();

  const barChartData = useMemo(
    () => [
      { name: 'Income', data: monthlyData.map((m) => m.income) },
      { name: 'Expense', data: monthlyData.map((m) => m.expense) },
    ],
    [monthlyData]
  );

  const barChartOptions = useMemo(
    () => ({
      ...barChartOptionsConsumption,
      xaxis: {
        ...barChartOptionsConsumption.xaxis,
        categories: monthlyData.map((m) => m.month),
      },
    }),
    [monthlyData]
  );

  const lineChartData = useMemo(
    () => [{ name: 'Balance', data: balanceOverTime.map((b) => b.balance) }],
    [balanceOverTime]
  );

  const lineChartOptions = useMemo(
    () => ({
      ...lineChartOptionsTotalSpent,
      xaxis: {
        ...lineChartOptionsTotalSpent.xaxis,
        categories: balanceOverTime.map((b) => b.date),
      },
    }),
    [balanceOverTime]
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <FinanceFilters />

      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="20px" mb="20px">
        <Card p="20px" minH="360px">
          <Box color={textColor} fontSize="lg" fontWeight="700" mb="16px">
            Monthly Income vs Expense
          </Box>
          {monthlyData.length > 0 ? (
            <Box h="280px">
              <BarChart chartData={barChartData} chartOptions={barChartOptions} />
            </Box>
          ) : (
            <Box
              h="280px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="secondaryGray.600"
              fontSize="sm"
              fontWeight="500"
            >
              Add transactions to see this chart
            </Box>
          )}
        </Card>
        <Card p="20px" minH="360px">
          <Box color={textColor} fontSize="lg" fontWeight="700" mb="16px">
            Balance Over Time
          </Box>
          {balanceOverTime.length > 0 ? (
            <Box h="280px">
              <LineChart
                chartData={lineChartData}
                chartOptions={lineChartOptions}
              />
            </Box>
          ) : (
            <Box
              h="280px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="secondaryGray.600"
              fontSize="sm"
              fontWeight="500"
            >
              Add transactions to see this chart
            </Box>
          )}
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="20px" mb="20px">
        <CategoryPieCard type="expense" title="Your Pie Chart" />
        <CategoryPieCard type="income" title="Your Pie Chart" />
      </SimpleGrid>
    </Box>
  );
}
